import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

// ─────────────────────────────────────────────────────────────────────────────
// Pill — generic status/label chip primitive.
// ─────────────────────────────────────────────────────────────────────────────

export type PillColor = "green" | "amber" | "red" | "grey" | "blue";

export interface PillProps {
  color: PillColor;
  /** Show a leading colour dot */
  dot?: boolean;
  children: ReactNode;
  className?: string;
}

const BASE =
  "inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full border whitespace-nowrap";

const COLOR_CLASSES: Record<PillColor, string> = {
  green: "bg-green-soft text-green-text border-[#a7f3d0]",
  amber: "bg-amber-soft text-amber-text border-[#fde9c8]",
  red: "bg-red-soft text-red-text border-[#fcc8c2]",
  grey: "bg-surface-2 text-muted border-border",
  blue: "bg-accent-soft text-accent border-[#c7d2fe]",
};

const DOT_CLASSES: Record<PillColor, string> = {
  green: "bg-green",
  amber: "bg-amber",
  red: "bg-red",
  grey: "bg-muted",
  blue: "bg-accent",
};

export function Pill({ color, dot, children, className }: PillProps): JSX.Element {
  return (
    <span className={cn(BASE, COLOR_CLASSES[color], className)}>
      {dot && (
        <span
          aria-hidden
          className={cn("w-1.5 h-1.5 rounded-full shrink-0", DOT_CLASSES[color])}
        />
      )}
      {children}
    </span>
  );
}
